/**
 * Phone text -> MATCH key ("is this the same number?"), read the same way as the shared dial rule.
 *
 * Two jobs, two functions (L-1216): phoneHref decides what to DIAL and gives nothing when unsure;
 * this decides whether two cells hold the same North American number. Before it, five server
 * normalisers kept every digit, so "831-555-1234 x12" keyed as "831555123412" (or its last ten,
 * "1555123412") and never matched "(831) 555-1234". Measured 2026-10-15 on live contacts: 184
 * phones carry an extension; four of the five rules keyed 150-182 of them wrong.
 *
 * @module lib/phone/phoneMatchKey
 */
import { phoneHref } from './phoneHref';

const NORTH_AMERICAN_TEL = /^tel:\+1(\d{10})$/;

/**
 * The 10 digits of the FIRST North American number in a phone cell, read exactly as phoneHref
 * reads it (an extension or a second number is dropped), or null when the cell holds no North
 * American number that can be read with certainty: an international number, a 7-digit local
 * number, free text. On null each caller keeps its own policy (e.g. identity matching still uses
 * a long international number as a signal).
 *
 * @param raw the phone text (anything else gives null)
 * @returns ten digits, or null
 */
export function phoneMatchKey(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const match = NORTH_AMERICAN_TEL.exec(phoneHref('tel', raw));
  return match ? match[1] : null;
}
